import type { VisitWindow } from '../../../src/engine/visits';
import type { FamilyStop } from '../../../src/publish/payload';
import photosJson from './photos.json';
import { own, placeBySlug, placeForPorts, type Photo, type Place } from './places';

/**
 * A page of the family site: one country (Micronesia: one state). Its stops come from the live plan, so a
 * new stop lands on the right page by itself; the island stories in places.ts become its sections.
 */
/** `feature`: a full-width chapter on the home page while the country is in the plan. */
export type Destination = { slug: string; name: string; country: string; tagline: string; intro: string[]; hero: Photo | null; feature?: { headline: string; text: string; photo: Photo } };

const storyHero = (slug: string) => placeBySlug.get(slug)?.photos[0] ?? null;
const storyPhoto = (slug: string, end: string) => placeBySlug.get(slug)!.photos.find(p => p.file.endsWith(end))!;
const commons = (key: string) => (photosJson as Record<string, Photo[]>)[key]?.[0] ?? null;

/** In sailing order. */
export const DESTINATIONS: Destination[] = [
  {
    slug: 'new-caledonia', name: 'New Caledonia', country: 'New Caledonia (France)', hero: commons('new-caledonia-hero'),
    tagline: 'Where the next leg begins: one of the biggest lagoons on Earth, and French bakeries.',
    intro: [
      'New Caledonia is a French corner of the South Pacific, ringed by a barrier reef that encloses one of the largest lagoons in the world. COS sailed in from Fiji in October 2026 and is in Nouméa getting ready for the next leg, west to Vanuatu.',
      'Come before we leave for croissants on the waterfront and turtles in the lagoon.',
    ],
  },
  {
    slug: 'vanuatu', name: 'Vanuatu', country: 'Vanuatu', hero: own('vanuatu-beach', 'A white-sand bay with outrigger canoes and turquoise water'),
    feature: { headline: 'Stand on the rim of a live volcano.', text: 'Live volcanoes, kastom villages and white-sand bays.', photo: own('tanna-eruption', 'Watching the volcano erupt at night from the crater rim') },
    tagline: 'Live volcanoes, kastom villages and Champagne Beach.',
    intro: [
      'Vanuatu is a Y-shaped chain of more than 80 islands and one of the most traditional places left in the Pacific, with live volcanoes, deep forest and some of the best beaches anywhere.',
      "Villages still live by kastom, the islanders' own law and custom. You ask before you walk on a beach, trade fruit for fish hooks, and get invited to dances that are not put on for tourists.",
    ],
  },
  {
    slug: 'solomons', name: 'Solomon Islands', country: 'Solomon Islands', hero: own('solomons-canoe', 'Children paddling a dugout canoe across clear turquoise water'),
    tagline: 'Remote reef islands, a famous lagoon, shipwrecks and almost no other visitors.',
    intro: [
      'The Solomons feel like the Pacific of a century ago: few roads, fewer visitors, and villagers who paddle out in dugout canoes to trade fruit and carvings.',
      'The water holds some of the richest reefs on the planet and the wrecks of the 1942–43 Guadalcanal campaign.',
    ],
  },
  {
    slug: 'pohnpei', name: 'Pohnpei', country: 'Federated States of Micronesia', hero: storyHero('pohnpei'),
    tagline: 'Back north of the equator for the first time since Panama: waterfalls, rainforest and a stone city on the reef.',
    intro: [
      'Pohnpei is the wettest and greenest state of Micronesia, with mountains that catch the clouds and dozens of waterfalls. Off its east coast lie the ruins of Nan Madol, a city of basalt logs built on the reef eight centuries ago.',
      'It is a good place to stay a while: friendly, green and full of things to explore.',
    ],
  },
  {
    slug: 'kosrae', name: 'Kosrae', country: 'Federated States of Micronesia', hero: storyHero('pohnpei'),
    tagline: 'The quiet, mountainous island at the eastern end of Micronesia.',
    intro: ['Kosrae is the smallest and quietest state of Micronesia: steep green mountains, mangrove channels, fringing reefs and the stone ruins of Lelu.'],
  },
  {
    slug: 'chuuk', name: 'Chuuk', country: 'Federated States of Micronesia', hero: storyHero('chuuk-outer'),
    tagline: 'A lagoon full of wartime wrecks, and an atoll where navigators still read the stars.',
    intro: [
      'Chuuk is a great lagoon ringed by reef, with high islands inside it and a sunken Second World War fleet on its floor. Beyond the reef lie the outer atolls, where life still follows the canoe and the season.',
    ],
  },
  {
    slug: 'yap', name: 'Yap', country: 'Federated States of Micronesia', hero: storyHero('yap-ulithi'),
    tagline: 'Stone money, manta rays and outer islands that keep the old ways.',
    intro: [
      'Yap State stretches from Yap itself east across hundreds of miles of ocean and a string of atolls. It is one of the most traditional places in the Pacific: stone money still lines village paths and the outer islands are run by their chiefs.',
    ],
  },
  {
    slug: 'palau', name: 'Palau', country: 'Republic of Palau', hero: storyHero('palau'),
    feature: { headline: 'Hundreds of islands, one lagoon.', text: 'Jungle-topped rock islands, sheltered anchorages and jellyfish that cannot sting. This is where we slow right down.', photo: storyPhoto('palau', '/5.jpg') },
    tagline: 'Mushroom islands, a lake of golden jellyfish and some of the best diving on the planet.',
    intro: [
      "Palau's Rock Islands are hundreds of jungle-topped limestone domes in a turquoise lagoon, with sheltered anchorages between them. It is a place to slow down: snorkel the walls, kayak the channels and swim with jellyfish that cannot sting.",
    ],
  },
  {
    slug: 'raja-ampat', name: 'Raja Ampat', country: 'Indonesia', hero: storyHero('raja-ampat'),
    feature: { headline: 'The richest reefs on Earth.', text: 'Jungle islands at the edge of New Guinea, and more kinds of coral and fish than anywhere else.', photo: storyHero('raja-ampat')! },
    tagline: 'The richest reefs on Earth, among jungle islands at the edge of New Guinea.',
    intro: [
      'Raja Ampat, the Four Kings, is a scatter of karst islands off the western tip of New Guinea with more kinds of coral and reef fish than anywhere else on Earth. Every Indonesian stop on our way is on this page.',
    ],
  },
  {
    slug: 'philippines', name: 'Philippines', country: 'Philippines', hero: own('el-nido-island', 'A small island with a white-sand spit and a bangka boat off El Nido'),
    tagline: 'Limestone islands, hidden lagoons, white sand and very good food.',
    intro: [
      'The Philippines are 7,000 islands of white sand, warm people and very good food, and Palawan, with its limestone cliffs and hidden lagoons, is the most beautiful of them all.',
    ],
  },
  {
    slug: 'borneo', name: 'Borneo', country: 'Malaysia', hero: own('borneo-river', 'Elephants on a riverbank in the Borneo rainforest, watched from a small boat'),
    tagline: 'Orangutans, elephants on the river, Mount Kinabalu and the city of cats.',
    intro: [
      'Borneo is the third-largest island in the world, and its Malaysian coast is our last long stretch: rainforest, river wildlife, giant caves and two lively cities.',
    ],
  },
  {
    slug: 'singapore', name: 'Singapore', country: 'Singapore', hero: storyHero('singapore'),
    tagline: 'The finish line for this leg: hawker food, Supertrees and a celebration.',
    intro: ['Singapore is where this leg of the voyage ends: a city of hawker centres, gardens and one of the busiest harbours in the world.'],
  },
];

export const destinationBySlug = new Map(DESTINATIONS.map(d => [d.slug, d]));
const BY_PLACE: Record<string, string> = { NC: 'new-caledonia', VU: 'vanuatu', TEM: 'solomons', WSI: 'solomons', PW: 'palau', RA: 'raja-ampat', NMA: 'raja-ampat', PH: 'philippines', BOR: 'borneo', SG: 'singapore' };

/** The page a stop belongs on, from its planner place; Micronesia splits into its states by longitude. Null for a place with no page yet. */
export function destinationOf(stop: { place: string; lon: number }): Destination | null {
  const slug = stop.place === 'FSM' ? (stop.lon < 148 ? 'yap' : stop.lon < 155 ? 'chuuk' : stop.lon < 162 ? 'pohnpei' : 'kosrae') : BY_PLACE[stop.place];
  return (slug && destinationBySlug.get(slug)) || null;
}

/** The country page each island story sits on, so old island links can land on their section. */
export const STORY_PAGE: Record<string, string> = {
  'new-caledonia': 'new-caledonia', tanna: 'vanuatu', efate: 'vanuatu', 'malekula-ambrym': 'vanuatu', luganville: 'vanuatu', 'champagne-beach': 'vanuatu', banks: 'vanuatu',
  vanikoro: 'solomons', temotu: 'solomons', honiara: 'solomons', russells: 'solomons', marovo: 'solomons', gizo: 'solomons', arnavons: 'solomons', pohnpei: 'pohnpei', 'chuuk-outer': 'chuuk', 'yap-ulithi': 'yap',
  palau: 'palau', 'raja-ampat': 'raja-ampat', ternate: 'raja-ampat', cebu: 'philippines', 'el-nido': 'philippines', sabah: 'borneo', sarawak: 'borneo', singapore: 'singapore',
  // Stories since split by stop: old links land on their country page.
  'santo-banks': 'vanuatu', 'solomons-central': 'solomons', 'solomons-west': 'solomons',
};

export type Section = { story: Place | null; stops: FamilyStop[] };
export type DestinationPlan = { dest: Destination; stops: FamilyStop[]; sections: Section[]; from: string | null; to: string; windows: VisitWindow[] };

/**
 * The live plan as pages: countries in the order we first reach them, each with its stops, its stops grouped
 * under their island story (each story once, in order of first visit), its span and its visit windows.
 * Stops on no page are returned so the site can say so.
 */
export function planByDestination(stops: FamilyStop[], windows: VisitWindow[]): { pages: DestinationPlan[]; unplaced: FamilyStop[] } {
  const pages = new Map<string, DestinationPlan>(), unplaced: FamilyStop[] = [];
  for (const s of stops) {
    const dest = destinationOf(s);
    if (!dest) { unplaced.push(s); continue; }
    let page = pages.get(dest.slug);
    if (!page) { page = { dest, stops: [], sections: [], from: s.arrive, to: s.depart, windows: [] }; pages.set(dest.slug, page); }
    page.stops.push(s);
    page.to = s.depart;
    const story = placeForPorts([s.port]);
    const section = page.sections.find(x => x.story !== null && x.story === story);
    if (section) section.stops.push(s);
    else page.sections.push({ story, stops: [s] });
  }
  for (const page of pages.values()) {
    const ports = new Set(page.stops.map(s => s.port));
    page.windows = windows.filter(w => w.ports.some(p => ports.has(p)));
  }
  return { pages: [...pages.values()], unplaced };
}
